import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Table, Form, InputGroup, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEnvelope } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import EmailModal from './EmailModal';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [sortBy, setSortBy] = useState('Name');
  const [sortDir, setSortDir] = useState('ASC');
  
  const { token } = useContext(AuthContext);
  
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await fetch('/api/customers/clients/all', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch clients');
        }
        
        const data = await response.json();
        setClients(data);
        setFilteredClients(data);
      } catch (err) {
        console.error('Error fetching clients:', err);
        toast.error('Failed to load clients');
        setError('Failed to load clients. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchClients();
  }, [token]);
  
  useEffect(() => {
    // Filter and sort clients
    let filtered = [...clients];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        client => 
          client.Name.toLowerCase().includes(term) ||
          (client.Email && client.Email.toLowerCase().includes(term)) ||
          (client.Address && client.Address.toLowerCase().includes(term)) ||
          (client.City && client.City.toLowerCase().includes(term)) ||
          (client.State && client.State.toLowerCase().includes(term)) ||
          (client.ZipCode && client.ZipCode.toLowerCase().includes(term)) ||
          (client.Phone && client.Phone.toLowerCase().includes(term))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // Handle date sorting
      if (sortBy === 'CreatedAt') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      // String comparison
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDir === 'ASC') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    setFilteredClients(filtered);
  }, [searchTerm, clients, sortBy, sortDir]);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleSortChange = (column) => {
    const newSortDir = column === sortBy && sortDir === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(column);
    setSortDir(newSortDir);
  };
  
  const handleEmailClient = (client) => {
    setSelectedClient(client);
    setShowEmailModal(true);
  };
  
  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setSelectedClient(null);
  };
  
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }
    
    setDeleteInProgress(true);
    setError('');
    
    try {
      const response = await fetch(`/api/customers/clients/${clientId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      let errorMessage = 'Failed to delete client';
      
      if (!response.ok) {
        // Try to parse error as JSON
        try {
          const data = await response.json();
          errorMessage = data.msg || errorMessage;
        } catch (parseError) {
          // If parsing fails, use response text
          errorMessage = await response.text() || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Update state by removing the deleted client
      setClients(clients.filter(client => client.ClientId !== clientId));
      setFilteredClients(filteredClients.filter(client => client.ClientId !== clientId));
      
      toast.success(data.msg || 'Client deleted successfully');
    } catch (err) {
      console.error('Error deleting client:', err);
      const errorMsg = err.message || 'Failed to delete client';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setDeleteInProgress(false);
    }
  };
  
  const getSortIndicator = (column) => {
    if (sortBy === column) {
      return sortDir === 'ASC' ? ' ↑' : ' ↓';
    }
    return '';
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading clients...</div>;
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Clients</h2>
        <Button as={Link} to="/clients/create" variant="primary">
          Add New Client
        </Button>
      </div>
      
      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      
      <Card className="mb-4">
        <Card.Body>
          <InputGroup>
            <Form.Control
              placeholder="Search clients by name, email, address, etc."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </InputGroup>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          {filteredClients.length > 0 ? (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th 
                      onClick={() => handleSortChange('Name')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Name{getSortIndicator('Name')}
                    </th>
                    <th 
                      onClick={() => handleSortChange('Email')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Email{getSortIndicator('Email')}
                    </th>
                    <th 
                      onClick={() => handleSortChange('Phone')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Phone{getSortIndicator('Phone')}
                    </th>
                    <th 
                      onClick={() => handleSortChange('City')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Address{getSortIndicator('City')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map(client => (
                    <tr key={client.ClientId}>
                      <td>{client.Name}</td>
                      <td>{client.Email || 'N/A'}</td>
                      <td>{client.Phone || 'N/A'}</td>
                      <td>
                        {client.Address ? (
                          <>
                            {client.Address}
                            {client.City && client.State ? 
                              <span>, {client.City}, {client.State} {client.ZipCode}</span> 
                              : ''
                            }
                          </>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        <Button
                          as={Link}
                          to={`/clients/${client.ClientId}`}
                          variant="primary"
                          size="sm"
                          className="me-2"
                        >
                          View
                        </Button>
                        {client.Email && (
                          <Button
                            variant="info"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEmailClient(client)}
                          >
                            <FaEnvelope className="me-1" /> Email
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClient(client.ClientId)}
                          disabled={deleteInProgress}
                        >
                          {deleteInProgress ? 'Deleting...' : 'Delete'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="text-center">
              {clients.length > 0 
                ? 'No clients match your search criteria.' 
                : 'No clients found. Add your first client.'}
            </p>
          )}
        </Card.Body>
      </Card>
      
      {/* Email Modal */}
      {selectedClient && (
        <EmailModal
          show={showEmailModal}
          handleClose={handleCloseEmailModal}
          clientName={selectedClient.Name}
          clientEmail={selectedClient.Email}
          clientId={selectedClient.ClientId}
        />
      )}
    </div>
  );
};

export default Clients;
